import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { RootLayout } from './app/layout';
import { LandingPage } from './app/page';
import { DashboardLayoutPage } from './app/(dashboard)/layout';
import { DashboardPage } from './app/(dashboard)/page';
import { ToolPage } from './app/tools/[slug]/page';
import { ToolsCatalogPage } from './app/tools/page';
import { AnalyticsPage } from './app/(dashboard)/analytics/page';
import { WorkspacesPage } from './app/(dashboard)/workspaces/page';
import { WorkspaceTopicsPage } from './app/(dashboard)/workspaces/topics/page';
import { WorkspaceDetailPage } from './app/(dashboard)/workspaces/[id]/page';
import { SettingsPage } from './app/(dashboard)/settings/page';
import { AdminPage } from './app/(dashboard)/admin/page';
import { AdminOrganizationsPage } from './app/(dashboard)/admin/organizations/page';
import { OrgDetailPage } from './app/(dashboard)/admin/organizations/[id]/page';

import { AdminEntitlementsPage } from './app/(dashboard)/admin/entitlements/page';
import { AdminJurisdictionsPage } from './app/(dashboard)/admin/jurisdictions/page';
import { AdminToolsPage } from './app/(dashboard)/admin/tools/page';
import { AdminEnginesPage } from './app/(dashboard)/admin/engines/page';
import { AdminSkillsPage } from './app/(dashboard)/admin/skills/page';
import { AdminAnalyticsPage } from './app/(dashboard)/admin/analytics/page';
import { AdminFeedbackPage } from './app/(dashboard)/admin/feedback/page';
import { AdminLogsPage } from './app/(dashboard)/admin/logs/page';
import { AdminSystemPage } from './app/(dashboard)/admin/system/page';
import { SourceRegistrationPage } from './app/(dashboard)/admin/source-registration/page';
import { SourcesPage } from './app/(dashboard)/sources/page';
import { SourceHealthPage } from './app/(dashboard)/sources/health/page';
import { PipelineReconcilePage } from './app/(dashboard)/sources/reconcile/page';
import { CivicScraperPage } from './app/(dashboard)/sources/civic-scraper/page';
import { ArchivePage } from './app/(dashboard)/archive/page';
import { NotificationsPage } from './app/(dashboard)/notifications/page';
import { CoveragePage } from './app/(dashboard)/coverage/page';
import { JurisdictionWorkspacesPage } from './app/(dashboard)/coverage/jurisdiction/page';
import { CoverageAuditPage } from './app/(dashboard)/coverage/audit/page';
import { NeedsReviewPage } from './app/(dashboard)/needs-review/page';

/** Redirects /dashboard/workspaces/:id → /workspaces/:id preserving the id param */
function RedirectWorkspaceDetail() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/workspaces/${id ?? ''}`} replace />;
}

/**
 * Redirects /workspaces?view=topic → /workspaces/topics
 * and /workspaces?view=jurisdiction → /coverage/jurisdiction.
 * All other visits render WorkspacesPage (My Workspaces).
 */
function WorkspacesOrRedirect() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');
  if (view === 'jurisdiction')
    return <Navigate to="/coverage/jurisdiction" replace />;
  if (view === 'topic') return <Navigate to="/workspaces/topics" replace />;
  return <WorkspacesPage />;
}

function App() {
  return (
    <BrowserRouter>
      <RootLayout>
        <Routes>
          {/* Landing Page */}
          <Route path="/" element={<LandingPage />} />

          {/*
           * Authenticated shell — DashboardLayoutPage provides the sidebar,
           * top-nav, auth guard, and WorkspaceContext for all child routes.
           */}
          <Route element={<DashboardLayoutPage />}>
            {/* Home */}
            <Route path="/dashboard" element={<DashboardPage />} />

            {/* Sidebar pages — no /dashboard/ prefix */}
            <Route path="/sources" element={<SourcesPage />} />
            <Route
              path="/sources/jobs"
              element={<Navigate to="/sources" replace />}
            />
            <Route path="/sources/health" element={<SourceHealthPage />} />
            <Route
              path="/sources/reconcile"
              element={<PipelineReconcilePage />}
            />
            <Route
              path="/sources/civic-scraper"
              element={<CivicScraperPage />}
            />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/workspaces" element={<WorkspacesOrRedirect />} />
            <Route
              path="/workspaces/topics"
              element={<WorkspaceTopicsPage />}
            />
            <Route path="/workspaces/:id" element={<WorkspaceDetailPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/coverage" element={<CoveragePage />} />
            <Route
              path="/coverage/jurisdiction"
              element={<JurisdictionWorkspacesPage />}
            />
            <Route
              path="/sources/coverage-audit"
              element={<CoverageAuditPage />}
            />
            <Route path="/needs-review" element={<NeedsReviewPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route
              path="/admin/organizations"
              element={<AdminOrganizationsPage />}
            />
            <Route
              path="/admin/organizations/:id"
              element={<OrgDetailPage />}
            />
            <Route
              path="/admin/entitlements"
              element={<AdminEntitlementsPage />}
            />
            <Route
              path="/admin/jurisdictions"
              element={<AdminJurisdictionsPage />}
            />
            <Route path="/admin/tools" element={<AdminToolsPage />} />
            <Route path="/admin/engines" element={<AdminEnginesPage />} />
            <Route path="/admin/skills" element={<AdminSkillsPage />} />
            <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
            <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
            <Route path="/admin/logs" element={<AdminLogsPage />} />
            <Route path="/admin/system" element={<AdminSystemPage />} />
            <Route
              path="/admin/source-registration"
              element={<SourceRegistrationPage />}
            />
            <Route path="/artifacts" element={<ArchivePage />} />

            {/* Legacy redirects — keep old /dashboard/* deep-links working */}
            <Route
              path="/dashboard/sources"
              element={<Navigate to="/sources" replace />}
            />
            <Route
              path="/dashboard/analytics"
              element={<Navigate to="/analytics" replace />}
            />
            <Route
              path="/dashboard/workspaces"
              element={<Navigate to="/workspaces" replace />}
            />
            <Route
              path="/dashboard/workspaces/:id"
              element={<RedirectWorkspaceDetail />}
            />
            <Route
              path="/dashboard/org-themes"
              element={<Navigate to="/notifications" replace />}
            />
            <Route
              path="/org-themes"
              element={<Navigate to="/notifications" replace />}
            />
            <Route
              path="/dashboard/coverage"
              element={<Navigate to="/coverage" replace />}
            />
            <Route
              path="/dashboard/settings"
              element={<Navigate to="/settings" replace />}
            />
            <Route
              path="/dashboard/admin"
              element={<Navigate to="/admin" replace />}
            />
            <Route
              path="/dashboard/archive"
              element={<Navigate to="/artifacts" replace />}
            />
            <Route
              path="/archive"
              element={<Navigate to="/artifacts" replace />}
            />
            <Route
              path="/dashboard/knowledge"
              element={<Navigate to="/artifacts" replace />}
            />

            {/* Tools — inside the authenticated shell */}
            <Route path="/tools" element={<ToolsCatalogPage />} />
            <Route path="/tools/:slug" element={<ToolPage />} />
            {/* Legacy URL redirect */}
            <Route
              path="/tools/ask-the-county"
              element={<Navigate to="/tools/on-the-record" replace />}
            />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RootLayout>
    </BrowserRouter>
  );
}

export default App;
